import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, Close } from '@mui/icons-material';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'Home', path: '/' },
    { label: 'Services', path: '/services' },
    { label: 'Our Work', path: '/work' },
    { label: 'About Us', path: '/about' },
    { label: 'Link Up', path: '/contact' }
  ];

  const isDarkHeroPage = !isScrolled && (location.pathname === '/' || location.pathname === '/services');

  return (
    <header
      className={`fixed z-[100] w-full transition-all duration-300 ${
        isScrolled
          ? 'top-3 left-3 right-3 rounded-full bg-white/15 backdrop-blur-md border border-white/25 shadow-lg'
          : 'top-0 left-0 right-0 rounded-none bg-transparent'
      }`}
    >
      <nav className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="flex-shrink-0"
          >
            <Link to="/" className="flex items-center">
              <img 
                src="/logo/New%20Bizani%20logo.png" 
                alt="Bizani Logo" 
                className={`h-12 w-auto transition-all duration-300 ${
                  isDarkHeroPage ? 'brightness-0 saturate-100' : ''
                }`}
                style={isDarkHeroPage ? {
                  filter: 'brightness(0) saturate(100%) invert(37%) sepia(90%) saturate(1234%) hue-rotate(346deg) brightness(98%) contrast(92%)'
                } : {}}
              />
            </Link>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navItems.map((item, index) => (
              <motion.div
                key={item.path}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link
                  to={item.path}
                  className={`px-4 py-2 text-sm font-ui font-semibold transition-colors duration-200 relative group ${
                    isScrolled 
                      ? 'text-white hover:text-bizani-orange' 
                      : isDarkHeroPage ? 'text-white hover:text-bizani-orange' : 'text-gray-700 hover:text-bizani-orange'
                  } ${location.pathname === item.path ? 'text-bizani-orange' : ''}`}
                >
                  {item.label}
                  <span className={`absolute bottom-0 left-0 h-0.5 bg-bizani-orange transition-all duration-300 ${
                    location.pathname === item.path ? 'w-full' : 'w-0 group-hover:w-full'
                  }`}></span>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`lg:hidden p-2 transition-colors ${
              isScrolled 
                ? 'text-white hover:text-bizani-orange' 
                : isDarkHeroPage ? 'text-white hover:text-bizani-orange' : 'text-gray-700 hover:text-bizani-orange'
            }`}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <Close className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Navigation - full width & height overlay */}
        <motion.div
          initial={false}
          animate={{
            opacity: isMenuOpen ? 1 : 0,
            visibility: isMenuOpen ? 'visible' : 'hidden'
          }}
          transition={{ duration: 0.3 }}
          className={`lg:hidden fixed top-0 left-0 right-0 bottom-0 w-screen h-screen min-h-[100dvh] z-[90] ${isMenuOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}
          style={{ width: '100vw', height: '100dvh', minHeight: '100dvh' }}
        >
          <div className={`absolute top-0 left-0 right-0 bottom-0 w-full h-full flex flex-col justify-center ${isScrolled || !isDarkHeroPage ? 'bg-black/95' : 'bg-black/90'}`} style={{ minHeight: '100dvh' }}>
            {/* Close X button - top right */}
            <button
              type="button"
              onClick={() => setIsMenuOpen(false)}
              className="absolute top-6 right-6 p-2 text-white hover:text-bizani-orange transition-colors z-10"
              aria-label="Close menu"
            >
              <Close className="w-8 h-8" />
            </button>
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMenuOpen(false)}
                className={`block px-6 py-4 text-lg font-ui font-semibold text-center transition-colors duration-200 ${
                  isScrolled || !isDarkHeroPage
                    ? 'text-white hover:bg-white/10 hover:text-bizani-orange' 
                    : 'text-white hover:bg-white/20'
                } ${location.pathname === item.path ? 'text-bizani-orange' : ''} ${location.pathname === item.path && !isDarkHeroPage ? 'bg-gray-50' : ''}`}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </motion.div>
      </nav>
    </header>
  );
};

export default Header;
